import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentIllustration,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import styles from './BulkArchiveBundleDialog.module.css'
import { byDesign } from '@/infra/design'
import imgArchiveItemUrl from '@/assets/misc/img_archive-item.svg'
import DocumentsDisabledIllustration from '@/components/SpotIllustration/DocumentsDisabledIllustration'

export interface BulkArchiveBundleDialogProps {
  showing: boolean
  onClose: () => unknown
  onArchive: () => unknown
  isArchivePending: boolean
  count: number
  corporateMode: boolean
  testId?: string
}

export function BulkArchiveBundleDialog({
  showing,
  onClose,
  onArchive,
  isArchivePending,
  count,
  corporateMode,
  testId,
}: BulkArchiveBundleDialogProps) {
  const noun = count === 1 ? 'bundle' : 'bundles'

  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isArchivePending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          {!corporateMode && (
            <ModalSheetContentIllustration
              src={imgArchiveItemUrl}
              illustration={byDesign(undefined, DocumentsDisabledIllustration)}
              alt="Archive bundles"
            />
          )}
          <h3 className={styles.title}>
            You’re about to archive {count} {noun}
          </h3>
          {getDescriptionDisplay(count)}
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Cancel"
            isDisabled={isArchivePending}
            onPress={onClose}
            variant="default"
            testId={testId && `${testId}-cancel-button`}
          />
          <Button
            label={`Archive ${count} ${noun}`}
            onPress={onArchive}
            isPending={isArchivePending}
            isDisabled={isArchivePending || count === 0}
            variant="destructive"
            testId={testId && `${testId}-confirm-button`}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

function getDescriptionDisplay(total: number) {
  if (total > 1) {
    return (
      <p className={styles.description}>
        Are you sure you want to archive these{' '}
        <span className={styles.count}>{total} bundles</span>? All future jobs
        will no longer be able to reference them. You can unarchive them later.
      </p>
    )
  }

  return (
    <p className={styles.description}>
      Are you sure you want to archive this{' '}
      <span className={styles.count}>{total} bundle</span>? All future jobs will
      no longer be able to reference it. You can unarchive it later.
    </p>
  )
}
